import dynamic from 'next/dynamic';
import type { ComponentType } from 'react';

import {
  ALL_INCLUSIVE_PUSH,
  BRAND_PROMISE,
  BRAND_SUPERIORITY,
  BUTTON_TICKET,
  CHECKLIST,
  COMPARISON_TABLE,
  CRISIS_ALERT,
  CRUISE_BOOKING,
  CRUISE_PROGRAM,
  DESTINATIONS,
  DOWNLOAD_LINKS_LIST,
  EDITORIAL_FOLDING_BLOCK,
  EVENT_ANIMATION,
  EVENT_FORM,
  EVENT_FORM_AFFINITY,
  EXCLUSIVE_COLLECTION_SPACE,
  EXCLUSIVE_COLLECTION_SPACE_INTRODUCTION,
  HAPPY_TO_CARE,
  HIGHLIGHT,
  IMMERSIVE,
  INSPIRATIONAL_GRID,
  LAST_SEEN_PRODUCTS,
  MEDIA_TEXT_HORIZONTAL,
  MEDIA_TEXT_VERTICAL,
  MEDIA_THUMBNAIL,
  MUST_TRY_EXPERIENCES,
  NAVIGATION_TAB,
  NAVIGATION_TABS,
  OFFER_DESCRIPTION,
  OFFERS_GRID,
  OFFER_LEGAL_INFORMATION,
  OFFERS_PUSH,
  OFFER_SEO,
  PAGE_TRACKING,
  PREBOOKING_FORM,
  PRODUCT_ACTIVITIES,
  PRODUCT_BREADCRUMB,
  PRODUCT_CHILDCARES,
  PRODUCT_IMAGE_MAP,
  PRODUCT_IMMERSIVE,
  PRODUCT_INFORMATION_SUMMARY,
  PRODUCT_PRICE_TICKET_CUSTOM,
  PRODUCT_SERVICES,
  PRODUCT_SIGNATURES,
  PRODUCT_STREET_VIEW,
  PRODUCT_STRENGTHS,
  PRODUCT_TAGS,
  PRODUCTS_FILTERS,
  QRCODE_PRODUCT_BARS,
  QRCODE_PRODUCT_DRESSCODE,
  QRCODE_PRODUCT_PROGRAM,
  QRCODE_PRODUCT_RESTAURANTS,
  QUOTATION_FORM,
  RELATED_PRODUCTS,
  RESORT_ACCESS_AND_MAP,
  RESORT_ACCOMMODATION_TYPES,
  RESORT_ACCOMMODATION_EXCLUSIVE_COLLECTION,
  RESORT_BARS,
  RESORT_BOOKING_BE,
  RESORT_DESTINATION,
  RESORT_DISTRICT,
  RESORT_FLASH_INFO,
  RESORT_GRID,
  RESORT_INTRODUCTION,
  RESORT_MEETING_ROOM_INFO,
  RESORT_MULTIMEDIA,
  RESORT_PRICE_REMOTE,
  RESORT_PRICE_TICKET,
  RESORT_RECOMMENDATIONS,
  RESORT_RESTAURANTS,
  RESORT_SEO,
  RESORT_SUMMARY,
  RESORT_TRIPADVISOR,
  SECTION,
  SECTIONS,
  SMART_FAQ,
  SUBMISSION_STATUS,
  TAB,
  TABS,
  TEASER_CARDS,
  TEST_AB,
  TEXT,
  THEMATIC,
  TITLE_AND_TEXT,
  TOUR_INTRODUCTION,
  TOUR_PRACTICAL_INFORMATION,
  TOUR_PROGRAM,
  TRAVEL_RESTRICTIONS,
  TRIPADVISOR_TESTIMONY,
  VIDEO,
} from './constants';

// const mod = ({ Component }) => Component;

type DescriptorLoader = () => Promise<{ default: ComponentType<unknown> }>;

export const descriptors = new Map<string, DescriptorLoader | ComponentType<unknown>>([
  /* layout lines */
  [PAGE_TRACKING, dynamic(() => import('./tracking/descriptor'))],

  /* common lines */
  [ALL_INCLUSIVE_PUSH, dynamic(() => import('./all-inclusive-push/descriptor'))],
  [BRAND_PROMISE, dynamic(() => import('./brand-promise/descriptor'))],
  [BRAND_SUPERIORITY, dynamic(() => import('./brand-superiority/descriptor'))],
  [BUTTON_TICKET, dynamic(() => import('./button-ticket/descriptor'))],
  [CHECKLIST, dynamic(() => import('./checklist/descriptor'))],
  [COMPARISON_TABLE, dynamic(() => import('./comparison-table/descriptor'))],
  [CRISIS_ALERT, dynamic(() => import('./crisis-alert/descriptor'))],
  [DESTINATIONS, dynamic(() => import('./destinations/descriptor'))],
  [DOWNLOAD_LINKS_LIST, dynamic(() => import('./download-links-list/descriptor'))],
  [EDITORIAL_FOLDING_BLOCK, dynamic(() => import('./editorial-folding-block/descriptor'))],
  [EVENT_ANIMATION, dynamic(() => import('./event-animation/descriptor'))],
  [EVENT_FORM, dynamic(() => import('./event-form/descriptor'))],
  [EVENT_FORM_AFFINITY, dynamic(() => import('./event-form-affinity/descriptor'))],
  [IMMERSIVE, dynamic(() => import('./immersive/descriptor'))],
  [INSPIRATIONAL_GRID, dynamic(() => import('./inspirational-grid/descriptor'))],
  [LAST_SEEN_PRODUCTS, dynamic(() => import('./last-seen-products/descriptor'))],
  [MEDIA_TEXT_HORIZONTAL, dynamic(() => import('./media-text-horizontal/descriptor'))],
  [MEDIA_TEXT_VERTICAL, dynamic(() => import('./media-text-vertical/descriptor'))],
  [MEDIA_THUMBNAIL, dynamic(() => import('./media-thumbnail/descriptor'))],
  [NAVIGATION_TAB, dynamic(() => import('./navigation-tab/descriptor'))],
  [NAVIGATION_TABS, dynamic(() => import('./navigation-tabs/descriptor'))],
  [OFFERS_GRID, dynamic(() => import('./offers-grid/descriptor'))],
  [OFFERS_PUSH, dynamic(() => import('./offers-push/descriptor'))],
  [PREBOOKING_FORM, dynamic(() => import('./prebooking-form/descriptor'))],
  [PRODUCT_TAGS, dynamic(() => import('./product-tags/descriptor'))],
  [PRODUCTS_FILTERS, dynamic(() => import('./products-filters/descriptor'))],
  [RESORT_GRID, dynamic(() => import('./products-grid/descriptor'))],
  [SECTION, dynamic(() => import('./section/descriptor'))],
  [SECTIONS, dynamic(() => import('./sections/descriptor'))],
  [SMART_FAQ, dynamic(() => import('./smart-faq/descriptor'))],
  [SUBMISSION_STATUS, dynamic(() => import('./submission-status/descriptor'))],
  [TAB, dynamic(() => import('./tab/descriptor'))],
  [TABS, dynamic(() => import('./tabs/descriptor'))],
  [TEASER_CARDS, dynamic(() => import('./teaser-cards/descriptor'))],
  [TEST_AB, dynamic(() => import('./test-ab/descriptor'))],
  [TEXT, dynamic(() => import('./text/descriptor'))],
  [THEMATIC, dynamic(() => import('./thematic/descriptor'))],
  [TITLE_AND_TEXT, dynamic(() => import('./title-and-text/descriptor'))],
  [TRAVEL_RESTRICTIONS, dynamic(() => import('./travel-restrictions/descriptor'))],
  [TRIPADVISOR_TESTIMONY, dynamic(() => import('./tripadvisor-testimony/descriptor'))],
  [VIDEO, dynamic(() => import('./video/descriptor'))],

  /* home lines : homepage recommended */
  [HIGHLIGHT, dynamic(() => import('./highlight/descriptor'))],

  /* offer lines : offerId needed */
  [OFFER_DESCRIPTION, dynamic(() => import('./offer-description/descriptor'))],
  [OFFER_LEGAL_INFORMATION, dynamic(() => import('./offer-legal-information/descriptor'))],
  [OFFER_SEO, dynamic(() => import('./offer-seo/descriptor'))],

  /* cruise lines : resortId/productId needed (only cruise ID) */
  [CRUISE_BOOKING, dynamic(() => import('./cruise-booking/descriptor'))],
  [CRUISE_PROGRAM, dynamic(() => import('./cruise-program/descriptor'))], // no ID needed but commonly on cruise page

  /* product lines : resortId/productId needed */
  [EXCLUSIVE_COLLECTION_SPACE, dynamic(() => import('./exclusive-collection-space/descriptor'))],
  [
    EXCLUSIVE_COLLECTION_SPACE_INTRODUCTION,
    dynamic(() => import('./exclusive-collection-space-introduction/descriptor')),
  ],
  [HAPPY_TO_CARE, dynamic(() => import('./happy-to-care/descriptor'))],
  [RESORT_ACCESS_AND_MAP, dynamic(() => import('./product-access-and-map/descriptor'))],
  [RESORT_ACCOMMODATION_TYPES, dynamic(() => import('./product-accommodations/descriptor'))],
  [
    RESORT_ACCOMMODATION_EXCLUSIVE_COLLECTION,
    dynamic(() => import('./product-accommodations-exclusive/descriptor')),
  ],
  [PRODUCT_ACTIVITIES, dynamic(() => import('./product-activities/descriptor'))],
  [RESORT_BARS, dynamic(() => import('./product-bars/descriptor'))],
  [RESORT_BOOKING_BE, dynamic(() => import('./product-booking/descriptor'))],
  [PRODUCT_BREADCRUMB, dynamic(() => import('./product-breadcrumb/descriptor'))],
  [PRODUCT_CHILDCARES, dynamic(() => import('./product-childcares/descriptor'))],
  [RESORT_DESTINATION, dynamic(() => import('./product-destination/descriptor'))],
  [RESORT_DISTRICT, dynamic(() => import('./resort-district/descriptor'))],
  [RESORT_FLASH_INFO, dynamic(() => import('./product-flash-info/descriptor'))],
  [PRODUCT_STREET_VIEW, dynamic(() => import('./product-google-street-view/descriptor'))],
  [PRODUCT_IMAGE_MAP, dynamic(() => import('./product-image-map/descriptor'))],
  [PRODUCT_IMMERSIVE, dynamic(() => import('./product-immersive/descriptor'))],
  [PRODUCT_INFORMATION_SUMMARY, dynamic(() => import('./product-information-summary/descriptor'))],
  [RESORT_INTRODUCTION, dynamic(() => import('./product-introduction/descriptor'))],
  [RESORT_MEETING_ROOM_INFO, dynamic(() => import('./product-meeting-room-info/descriptor'))],
  [RESORT_MULTIMEDIA, dynamic(() => import('./product-multimedia/descriptor'))],
  [MUST_TRY_EXPERIENCES, dynamic(() => import('./product-must-try-experiences/descriptor'))],
  [RESORT_PRICE_REMOTE, dynamic(() => import('./product-price-remote/descriptor'))],
  [RESORT_PRICE_TICKET, dynamic(() => import('./product-price-ticket/descriptor'))],
  [PRODUCT_PRICE_TICKET_CUSTOM, dynamic(() => import('./product-price-ticket-custom/descriptor'))],
  [QRCODE_PRODUCT_BARS, dynamic(() => import('./qrcode-product-bars/descriptor'))],
  [QRCODE_PRODUCT_DRESSCODE, dynamic(() => import('./qrcode-product-dresscode/descriptor'))],
  [QRCODE_PRODUCT_PROGRAM, dynamic(() => import('./qrcode-product-program/descriptor'))],
  [QRCODE_PRODUCT_RESTAURANTS, dynamic(() => import('./qrcode-product-restaurants/descriptor'))],
  [QUOTATION_FORM, dynamic(() => import('./product-quotation-form/descriptor'))],
  [RESORT_RECOMMENDATIONS, dynamic(() => import('./product-recommendations/descriptor'))],
  [RELATED_PRODUCTS, dynamic(() => import('./product-related-products/descriptor'))],
  [RESORT_RESTAURANTS, dynamic(() => import('./product-restaurants/descriptor'))],
  [RESORT_SEO, dynamic(() => import('./product-seo-title/descriptor'))],
  [PRODUCT_SERVICES, dynamic(() => import('./product-services/descriptor'))], // no ID but commonly on product page
  [PRODUCT_SIGNATURES, dynamic(() => import('./product-signatures/descriptor'))], // no ID but commonly on product page
  [PRODUCT_STRENGTHS, dynamic(() => import('./product-strengths/descriptor'))],
  [RESORT_SUMMARY, dynamic(() => import('./product-summary/descriptor'))],
  [RESORT_TRIPADVISOR, dynamic(() => import('./product-tripadvisor/descriptor'))],

  /* tour lines : resortId/productId needed (only tour ID) */
  [TOUR_INTRODUCTION, dynamic(() => import('./tour-introduction/descriptor'))],
  [TOUR_PRACTICAL_INFORMATION, dynamic(() => import('./tour-practical-information/descriptor'))],
  [TOUR_PROGRAM, dynamic(() => import('./tour-program/descriptor'))],
]);

export const hasComponent = (name) => {
  return descriptors.has(name);
};

export const getComponent = (name) => {
  if (!descriptors.has(name)) {
    return null;
  }

  return descriptors.get(name);
};
